/**
 * error-logger
 *
 * Description:
 * Common function to send caught client side errors to the backend.
 * Fire-and-forget: never throws, never blocks the UI flow.
 *
 * Two ways in:
 *  - components/ErrorBoundary.jsx, for errors thrown during render
 *  - by hand from a catch block, for errors React error boundaries cannot
 *    see, such as errors inside event handlers or async code
 */

import { store } from "./../store/index";
import { errorLogActions } from "./../store/errorLog.slice";
import { getOrCreateSessionId } from "./activity-logger";

// Same error is not resent inside this window. A render loop can fire
// componentDidCatch many times a second, and without this guard it would
// flood the log endpoint.
const DUPLICATE_WINDOW_MS = 10000;
let lastErrorKey = null;
let lastErrorAt = 0;

/**
 * Drop the scheme and host from anything url shaped, keeping the path.
 *
 * Two reasons. The host adds nothing to a stack trace, and the AWS WAF in
 * front of the api blocks request bodies containing "://" as a possible
 * remote file inclusion attempt.
 */
export const stripOrigin = (text) =>
  String(text).replace(/[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/\s)]*/g, "");

/* Clean up and keep stacks from making the request body huge */
const truncate = (text, maxLength) =>
  text ? stripOrigin(text).slice(0, maxLength) : null;

/**
 * Pull the real failing component out of the React component stack.
 *
 * The stack looks like "\n    at CheckingIn\n    at Common\n    at ThemeLayout".
 * The first frame starting with a capital letter is the component that threw.
 * Lower case frames are plain html tags, so they are skipped. This is why one
 * error boundary is enough: the name does not have to come from the boundary.
 */
const componentFromStack = (componentStack) => {
  if (!componentStack) return null;
  const frames = componentStack.split("\n");
  for (let i = 0; i < frames.length; i++) {
    const match = frames[i].trim().match(/^at\s+([A-Z][A-Za-z0-9_]*)/);
    if (match) return match[1];
  }
  return null;
};

/**
 * Send one error to the backend.
 *
 * @param {Error|string} error     The caught error.
 * @param {string} [component]     Name of the page or component that failed.
 * @param {Object} [errorInfo]     React info object, holds componentStack.
 * @param {string} [type]          Error category, defaults to RUNTIME_ERROR.
 * @param {Object} [extra]         Extra fields merged into the log entry, used
 *                                 by the global handlers to add the api url or
 *                                 the script location that failed.
 */
export const logError = (
  error,
  component = "Unknown",
  errorInfo = null,
  type = "RUNTIME_ERROR",
  extra = null
) => {
  try {
    const message = error?.message || String(error || "Unknown error");
    // The stack knows better than the boundary which component actually broke
    const componentName = componentFromStack(errorInfo?.componentStack) || component;

    const key = `${componentName}::${message}::${extra?.api_url || ""}`;
    const now = Date.now();
    if (key === lastErrorKey && now - lastErrorAt < DUPLICATE_WINDOW_MS) return;
    lastErrorKey = key;
    lastErrorAt = now;

    const { checkIn } = store.getState();

    const payload = {
      session_id: getOrCreateSessionId(),
      log_type: "GUEST",
      environment: process.env.REACT_APP_CUSTOM_NODE_ENV || "unknown",
      logs: [
        {
          timestamp: new Date().toISOString(),
          severity: type === "COMPONENT_ERROR" ? "critical" : "error",
          error_type: type,
          message: message,
          listing_slug: checkIn?.listingSlug || null,
          booking_slug: checkIn?.bookingSlug || null,
          // Path and query only, no origin. The host adds nothing useful to
          // the log and makes the entries harder to read.
          page: `${window.location.pathname}${window.location.search}`,
          route: window.location.pathname,
          user_agent: navigator.userAgent || null,
          component: {
            name: componentName,
            // Where the boundary sits, kept so a render error can be placed
            // in the tree. Null for errors reported from a catch block.
            boundary: errorInfo ? component : null,
            component_stack: errorInfo?.componentStack,
            error_stack: error?.stack,
          },
          // Extra fields from the global handlers, e.g. the failing api url
          ...(extra || {}),
        },
      ],
    };

    store.dispatch(errorLogActions.logClientError(payload));
  } catch (loggingError) {
    // Never let the logger itself break the app
    console.error("Client error log failed:", loggingError);
  }
};

/**
 * Handle a promise rejection that no catch block picked up.
 *
 * The net for async failures React error boundaries cannot see: a thunk that
 * throws, an await inside an event handler, a timer callback.
 *
 * Failed api calls do not arrive here. Redux Toolkit catches the throw inside
 * the thunk, so those are logged in helpers/fetch-wrapper.js instead.
 *
 * @param {PromiseRejectionEvent} event
 */
export const logUnhandledRejection = (event) => {
  const reason = event?.reason;
  const error = reason instanceof Error ? reason : new Error(reason?.message || String(reason));

  logError(error, "UnhandledRejection", null, "UNHANDLED_REJECTION");
};

/**
 * Handle a javascript error that reached the window.
 *
 * Covers anything thrown outside React render: event handlers, setTimeout and
 * setInterval callbacks, third party scripts such as Stripe.
 *
 * @param {ErrorEvent} event
 */
export const logWindowError = (event) => {
  // Browsers hide details of errors thrown by cross origin scripts and only
  // give "Script error." with no file or line. Nothing useful to log.
  if (!event?.error && !event?.filename) return;

  const error = event.error instanceof Error ? event.error : new Error(event.message || "Unknown script error");
  const source = event.filename
    ? { script_source: truncate(`${event.filename}:${event.lineno}:${event.colno}`, 500) }
    : null;

  logError(error, "Window", null, "JS_ERROR", source);
};

/**
 * Register both handlers. Called once from src/index.js before anything
 * renders, so errors thrown while the app is still starting are caught too.
 */
export const initGlobalErrorHandlers = () => {
  window.addEventListener("unhandledrejection", logUnhandledRejection);
  window.addEventListener("error", logWindowError);
};
